import { isPlatformServer } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ROUTER_NAVIGATED, RouterNavigatedAction, getRouterSelectors } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { EMPTY, filter, map, of, switchMap } from 'rxjs';
import { DealerFeature, TrackingFeature } from '..';
import { isHWS } from '../../providers/platform.token';
import { EventTrackingService } from '../../services/tracking.service';
import {
    ApplicationName,
    DataLayerEntry,
    PageloadStartLayer,
    filterTargetTracking,
    injectTrackingEnv,
    mapDealerFromVehicleForTracking,
    mapModelsForTracking,
    mapVehicleForTracking,
} from '../../utilities/tracking';
import { actions } from './tracking.actions';
import { state } from './tracking.reducer';

@Injectable()
export class TrackingEffects {
    protected readonly actions$ = inject(Actions);
    protected readonly store = inject(Store);
    protected readonly platformId = inject(PLATFORM_ID);
    protected readonly trackingService = inject(EventTrackingService);
    protected readonly router = inject(Router);
    protected readonly isHWS = isHWS();
    protected readonly trackingEnv = injectTrackingEnv();
    protected lastPathname: string | null = null;

    pushDataLayer$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(actions.eventTrackingPush),
                concatLatestFrom(() => this.store.select(state.selectDisableTracking)),
                map(([action, disableTracking]) => {
                    if (
                        disableTracking ||
                        (Object.keys(action.dataLayer).length === 0 && action.dataLayer.constructor === Object)
                    ) {
                        return EMPTY;
                    }

                    this.trackingService.pushDataLayer(action.dataLayer);
                    return EMPTY;
                }),
            );
        },
        { dispatch: false },
    );

    pageView$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ROUTER_NAVIGATED),
            filter((action: RouterNavigatedAction) => {
                const urlAfter = action.payload.event.urlAfterRedirects;
                const path = urlAfter.split('?')[0].split('#')[0];
                const shouldRunEffect = this.lastPathname !== path;
                this.lastPathname = path;
                return shouldRunEffect;
            }),
            concatLatestFrom(() => [
                this.store.select(getRouterSelectors().selectRouteData),
                this.isHWS ? this.store.select(DealerFeature.state.selectDealer) : of(null),
                this.isHWS ? this.store.select(DealerFeature.state.selectDealerName) : of(null),
            ]),
            switchMap(([, routeData, dealer, dealerName]) => {
                if (isPlatformServer(this.platformId)) {
                    return [actions.eventTrackingPush({ dataLayer: {} })];
                }

                let dataLayer: PageloadStartLayer = {
                    event: 'pageload_start',
                    environment: {
                        type: this.trackingEnv,
                        country: 'DE',
                        datalayerVersion: '9.3.1',
                    },
                    page: {
                        name: window.document.title,
                        contentType: routeData['contentType'] ?? '',
                        language: 'de',
                        application: {
                            name: dealer ? ApplicationName.HWS : ApplicationName.HCM,
                            version: '2',
                        },
                        componentName: routeData['componentName'] ?? undefined, // If applicable
                    },
                };

                if (dealer && dealerName) {
                    dataLayer = {
                        ...dataLayer,
                        dealer: {
                            name: dealerName,
                            zipcode: dealer.zip,
                            location: `${dealer.city}, Germany`,
                            code: dealer.sapId,
                        },
                    };
                }
                return [actions.eventTrackingPush({ dataLayer }), actions.resetModelsOnPageTracking()];
            }),
        );
    });

    PagesWithModelsPageViewCompleted$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.setModelsOnPageTracking),
            map((action) => {
                if (
                    isPlatformServer(this.platformId) ||
                    !action.models ||
                    (action.models && action.models.length === 0)
                ) {
                    return actions.eventTrackingPush({ dataLayer: {} });
                }

                const dataLayer = {
                    event: 'pageload_complete',
                    models: mapModelsForTracking(action.models),
                };

                return actions.eventTrackingPush({ dataLayer });
            }),
        );
    });

    clickTracking$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.clickTracking),
            concatLatestFrom(() => [this.store.select(TrackingFeature.state.selectCurrentModelsOnPage)]),
            map(([action, models]) => {
                if (!action.elType || !action.elTarget) {
                    return actions.eventTrackingPush({ dataLayer: {} });
                }

                const dataLayer: DataLayerEntry = {
                    event: action.event,
                    page: {
                        element: {
                            type: action.elType,
                            target: filterTargetTracking(action.elTarget),
                            label: action.elTarget.includes('tel:') ? `${action.elLabel} (phone)` : action.elLabel,
                        },
                    },
                };

                if (models && models.length > 0) {
                    dataLayer['models'] = mapModelsForTracking(models);
                }

                if (action.vehicle) {
                    dataLayer['models'] = mapVehicleForTracking([action.vehicle]);
                }

                return actions.eventTrackingPush({ dataLayer });
            }),
        );
    });

    overlayTracking$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.overlayTracking),
            map((action) => {
                if (!action.label) {
                    return actions.eventTrackingPush({ dataLayer: {} });
                }

                const dataLayer: DataLayerEntry = {
                    event: 'page_overlay',
                    page: {
                        overlay: action.label,
                    },
                };

                return actions.eventTrackingPush({ dataLayer });
            }),
        );
    });

    filterTracking$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.filterTracking),
            map((action) => {
                if (!action.filterType) {
                    return actions.eventTrackingPush({ dataLayer: {} });
                }

                const dataLayer: DataLayerEntry = {
                    event: 'filter_adjust',
                    filter: {
                        type: action.filterType,
                    },
                };

                return actions.eventTrackingPush({ dataLayer });
            }),
        );
    });

    formTestDriveStartTracking$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.formTestDriveStartTracking),
            map((action) => {
                const dataLayer: DataLayerEntry = {
                    event: 'testdrive_start',
                    testdrive: {
                        topic: 'used cars',
                    },
                    models: mapVehicleForTracking([action.vehicle]),
                };

                return actions.eventTrackingPush({ dataLayer });
            }),
        );
    });

    formGeneralRequestStartTracking$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.formGeneralRequestStartTracking),
            map((action) => {
                const dataLayer: DataLayerEntry = {
                    event: 'contact_start',
                    contact: {
                        topic: 'used cars',
                        subtopic: 'general enquiry',
                    },
                    models: mapVehicleForTracking([action.vehicle]),
                };

                return actions.eventTrackingPush({ dataLayer });
            }),
        );
    });

    formTestDriveSubmitTracking$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.formTestDriveSubmitTracking),
            map(({ form, vehicle, contact }) => {
                const dataLayer: DataLayerEntry = {
                    event: 'testdrive_complete',
                    testdrive: {
                        comment: form.comment,
                        date: form.date,
                        day: form.day,
                        time: form.time,
                        topic: 'used cars',
                    },
                    models: mapVehicleForTracking([vehicle]),
                    dealer: mapDealerFromVehicleForTracking(vehicle),
                    user: this.getUserTrackingData(contact),
                };

                return actions.eventTrackingPush({ dataLayer });
            }),
        );
    });

    formGeneralRequestSubmitTracking$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.formGeneralRequestSubmitTracking),
            map(({ contact, vehicle, form }) => {
                const dataLayer: DataLayerEntry = {
                    event: 'contact_complete',
                    contact: {
                        comment: form.comment,
                        topic: 'used cars',
                        subtopic: form.subtopic,
                    },
                    models: mapVehicleForTracking([vehicle]),
                    dealer: mapDealerFromVehicleForTracking(vehicle),
                    user: this.getUserTrackingData(contact),
                };

                return actions.eventTrackingPush({ dataLayer });
            }),
        );
    });

    getUserTrackingData(contact: { name: string; email: string; phone?: string; generalMarketing: boolean }) {
        return {
            consent: {
                generalMarketing: contact.generalMarketing,
            },
            contact: {
                email: contact.email,
                phone: contact.phone,
            },
            name: contact.name,
        };
    }
}
